import { AttachCommandAppIconResponse, SlashCommand, SlashCommandApp } from '@@types/Api/Command';
import Attachment from '@@types/Attachment';
import { CHANNEL_ID, SLASH_COMMAND_APP_ID } from '@@types/common';
import Form from '@@types/Form';
import Log, { CommandLog } from '@@types/Log';
import { Options } from '@popperjs/core';

import { PopperType, PROFILE_TYPE, SYNTAX_REGEX } from '~/constants';
import popupWindowService from '~/services/popupWindowService';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { api } from '~/stores/providers/Api2';
import { slashCommandAppsStore } from '~/stores/SlashCommandAppsStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { keyBy } from '~/utils/fnUtil/fnUtil';
import integrationAppUtil from '~/utils/integrationAppUtil';
import { debounce, isEmpty } from 'lodash';
import { runInAction } from 'mobx';
import { assoc, cond, curry, equals, T, tap } from 'ramda';

import Actions from '../Actions';
import { stores } from '../Stores';

class SlashCommandAppActions {
  public executeAction = curry(
    debounce((attachment: Attachment, log: Log, actionName: string, actionValue: string, action: string) => {
      if (action === 'openUrl' && SYNTAX_REGEX.meetingUrl.test(actionValue)) {
        Promise.all([api.common.getAuthToken(userStore.id), this.actions.memberActions.fetchMember(userStore.id)]).then(
          ([result]) => {
            const user = stores.user;
            popupWindowService.openMeeting(actionValue, user.emailAddress, user.name, result?.token ?? '');
          }
        );
        return;
      }
      if (action === 'openUrl') {
        window.open(actionValue, '_blank');
        return;
      }
      const timestamp = Date.now();
      slashCommandAppsStore.setCommandTimestamp(timestamp, log.channelId);
      api.command.executeAction({
        appId: attachment.appId,
        commandId: attachment.commandId,
        channelLogId: log.id,
        cmdToken: (log as CommandLog).cmdToken,
        actionName,
        actionValue,
        channelId: log.channelId,
        originalMessage: log as CommandLog,
        timestamp,
      });
    }, 300)
  );

  public executeCommand = curry((channelId: string, params: { text: string; name: string }) => {
    const { text, name } = params;
    const timestamp = Date.now();
    slashCommandAppsStore.setCommandTimestamp(timestamp, channelId);
    const command = slashCommandAppsStore.getChannelCommandByName(name, channelId);
    if (!command) {
      return;
    }
    const { integrationAppId: appId, id: commandId } = command;
    if (appId === userStore.meetingId || appId === userStore.webexId) {
      slashCommandAppsStore.setMeetingTriggeredChannel(channelId);
    }
    api.command.executeCommand({
      appId,
      commandId,
      channelId,
      commandRequest: text,
      timestamp,
    });
  });

  public executeCommandById = (channelId: string, params: { text: string; commandId: string }) => {
    const { text, commandId } = params;
    const timestamp = Date.now();
    slashCommandAppsStore.setCommandTimestamp(timestamp, channelId);
    const command = slashCommandAppsStore.getChannelCommandById(commandId, channelId);
    if (!command) {
      return;
    }
    const { integrationAppId: appId } = command;
    if (appId === userStore.meetingId || appId === userStore.webexId) {
      slashCommandAppsStore.setMeetingTriggeredChannel(channelId);
    }
    api.command.executeCommand({ appId, commandId, channelId, commandRequest: text, timestamp });
  };

  private commandRegisterPromise = null;
  private fetchCommandPromises: Record<string, Promise<SlashCommand>> = {};
  private fetchPromises: Record<string, Promise<SlashCommandApp>> = {};

  constructor(private actions: Actions) {}

  public createApp = async (form: Form) => {
    const { name, description } = form.values() as {
      name: string;
      description: string;
    };
    let app: SlashCommandApp = await api.commandApp.createIntegrationApp({ name, description });
    try {
      const icon = await this.updateIcon(form, app.id);
      app = Object.assign(app, { iconAttachId: icon.attachId });
    } catch (e) {}

    runInAction(() => {
      slashCommandAppsStore.addUserApp(app);
      slashCommandAppsStore.setActiveUserAppId(app.id);
    });
  };

  public createCommand = async (form) => {
    const params = { ...form.values(), appId: slashCommandAppsStore.activeUserAppId };
    const command = await api.command.createCommand(params);
    slashCommandAppsStore.addUserAppCommand(command);
  };

  public deleteUserAppCommand = async (command: SlashCommand) => {
    const { id: commandId, integrationAppId: appId } = command;
    await api.command.deleteCommand({ commandId, appId });
    slashCommandAppsStore.deleteUserAppCommand(command);
  };

  public deleteUserApp = async (appId: SLASH_COMMAND_APP_ID) => {
    await api.commandApp.deleteIntegrationApp(appId);
    runInAction(() => {
      slashCommandAppsStore.selectAppExceptAppId(appId);
      slashCommandAppsStore.deleteUserApp(appId);
    });
  };

  public updateUserAppCommand = async (command: Partial<SlashCommand>) => {
    const updatedCommand = await api.command.updateCommand(command);
    slashCommandAppsStore.updateUserAppCommand(updatedCommand);
  };

  public fetchRegistrableApps = async () => {
    const { integrationApps = [] } = await api.command.fetchRegistrableApps();
    slashCommandAppsStore.setRegistrableApps(keyBy('id', integrationApps));
  };

  public fetchChannelApps = async (channelId: CHANNEL_ID) => {
    const { integrationApps = [] } = await api.command.fetchChannelApps(channelId);
    slashCommandAppsStore.setChannelApps(channelId, integrationApps);
    this.validateMeetingService(integrationApps);
  };

  public fetchUserAppCommands = async (appId: SLASH_COMMAND_APP_ID) => {
    const { commands = [] } = await api.command.fetchCommands(appId);
    slashCommandAppsStore.setUserAppCommands(appId, commands);
  };

  public getOrFetchCommand = (commandId) => {
    const command = slashCommandAppsStore.allCommands[commandId];
    return command ? Promise.resolve(command) : this.fetchCommandById(commandId);
  };

  public getOrFetchApp = (appId: SLASH_COMMAND_APP_ID) => {
    const app = slashCommandAppsStore.getAppById(appId);
    return app ? Promise.resolve(app) : this.fetchAppById(appId);
  };

  public fetchAppById = (appId: string): Promise<SlashCommandApp> => {
    const fetchPromise = this.fetchPromises[appId] || api.commandApp.fetchAppById(appId);
    this.fetchPromises[appId] = fetchPromise;
    return fetchPromise.then(tap(slashCommandAppsStore.setApp)).finally(() => {
      delete this.fetchPromises[appId];
    });
  };

  public handleSelectApp = (appId: SLASH_COMMAND_APP_ID) => {
    // prettier-ignore
    cond([
      [equals('addApp'), this.openCreateAppModal],
      [T, () => slashCommandAppsStore.setActiveUserAppId(appId)]]
    )(appId);
  };

  public openCreateAppModal = () => {
    this.actions.modalActions.openIntegrationAppCreate();
  };

  public openCreateCommandModal = () => {
    this.actions.modalActions.openSlashCommandCreate({
      integrationAppId: slashCommandAppsStore.activeUserAppId,
    });
  };

  public openAppProfilePopper = async ({
    referenceNode,
    options,
    appId,
  }: {
    referenceNode: HTMLElement;
    options: Partial<Options>;
    appId: SLASH_COMMAND_APP_ID;
  }) => {
    const app = await this.getOrFetchApp(appId);
    if (isEmpty(app)) return;
    slashCommandAppsStore.setProfileAppId(appId);
    uiState.openPopper({
      name: PopperType.Profile,
      args: { referenceNode, options, type: PROFILE_TYPE.APP },
    });
  };

  public regenerateToken = async (appId) => {
    const token = await api.commandApp.generateToken(appId);
    slashCommandAppsStore.setToken(appId, token);
  };

  public toggleChannelCommand = (params) => {
    if (this.commandRegisterPromise) return;
    const isRegistered = slashCommandAppsStore.hasCommandByIdInChannel(params.command.id, params.channelId);
    this.commandRegisterPromise = isRegistered ? this.unregister(params) : this.register(params);
    this.commandRegisterPromise.finally(() => {
      this.commandRegisterPromise = null;
    });
  };

  public updateInteractiveMessage = async (values: {
    interactiveMessageUse: boolean;
    requestUrl: string;
    optionalUrl: string;
  }) => {
    const params = assoc('appId', slashCommandAppsStore.activeUserAppId, values);
    await api.commandApp.updateIntegrationAppInteractiveMessage(params);
    this.setInteractiveMessage(params);
  };

  private fetchCommandById = (commandId) => {
    const fetchPromise = this.fetchCommandPromises[commandId] || api.command.fetchCommandById(commandId);
    this.fetchCommandPromises[commandId] = fetchPromise;
    return fetchPromise.finally(() => {
      delete this.fetchCommandPromises[commandId];
    });
  };

  private register = (params: { channelId: string; command: SlashCommand }) => {
    return api.command
      .registerCommand(params)
      .then(equals('true'))
      .then(() => this.fetchChannelApps(params.channelId));
  };

  private unregister = (params) => {
    return api.command
      .unregisterCommand(params)
      .then(equals('ok'))
      .then(() => this.fetchChannelApps(params.channelId));
  };

  private updateIcon = async (form, appId): Promise<AttachCommandAppIconResponse> => {
    const iconAttachIdField = form.$('iconAttachId');
    if (isEmpty(iconAttachIdField.files)) {
      return Promise.reject();
    }

    const { files, value: iconId } = iconAttachIdField;
    const file = files[0];
    const params = { file, appId, iconId };

    return integrationAppUtil.isValidIcon(iconId)
      ? await api.commandApp.changeIcon(params)
      : await api.commandApp.attachIcon(params);
  };

  public updateApp = curry(async (appId: SLASH_COMMAND_APP_ID, form: Form) => {
    const { name, description } = form.values();
    const app = await api.commandApp.updateIntegrationApp({ name, description, appId });
    let icon;
    try {
      icon = await this.updateIcon(form, appId);
    } catch (error) {}
    slashCommandAppsStore.updateUserApp(icon ? { ...app, iconAttachId: icon.attachId } : app);
  });

  private validateMeetingService = (apps: SlashCommandApp[]) => {
    let meeting = false;
    let webex = false;
    for (const app of apps) {
      if (app.id === userStore.meetingId) {
        meeting = true;
      }
      if (app.id === userStore.webexId) {
        webex = true;
      }
      if (meeting && webex) {
        break;
      }
    }
    if (!meeting) {
      globalSettingStore.setMeetingEnabled(false);
    }
    if (!webex) {
      globalSettingStore.setWebexEnabled(false);
    }
  };

  private setInteractiveMessage = ({ appId, interactiveMessageUse, requestUrl, optionalUrl }) => {
    slashCommandAppsStore.updateUserApp({
      id: appId,
      interactiveMessageUse,
      optionalUrl,
      requestUrl,
    });
  };
}

export default SlashCommandAppActions;
